"use client";

/**
 * Product dialog facade — Radix-shaped API (`open` / `onOpenChange`,
 * `DialogContent`, `DialogTrigger`) on Headless UI + Motion transitions.
 * Implementation: `./headless-ui/*`. Sheet / AlertDialog stay on Radix.
 *
 * Default close control: CircleButton `xs` (`showCloseButton`, default true).
 */

import { Slot } from "@radix-ui/react-slot";
import { XIcon } from "lucide-react";
import {
	Children,
	cloneElement,
	createContext,
	isValidElement,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ComponentPropsWithoutRef,
	type CSSProperties,
	type HTMLAttributes,
	type ReactElement,
	type ReactNode,
} from "react";
import { CircleButton } from "./custom/circle-button";
import { cn } from "../lib/cn";
import {
	DialogClose as HeadlessDialogClose,
	DialogDescription as HeadlessDialogDescription,
	DialogPanel as HeadlessDialogPanelPrimitive,
	DialogRoot,
	DialogTitle as HeadlessDialogTitle,
	DialogBackdrop as HeadlessDialogBackdropPrimitive,
} from "./headless-ui/dialog-primitive";

interface DialogContextValue {
	open: boolean;
	setOpen: (open: boolean) => void;
}

const DialogContext = createContext<DialogContextValue | null>(null);

function useDialogContext(component: string): DialogContextValue {
	const ctx = useContext(DialogContext);
	if (!ctx) {
		throw new Error(`${component} must be used within <Dialog>`);
	}
	return ctx;
}

interface DialogProps {
	open?: boolean;
	defaultOpen?: boolean;
	onOpenChange?: (open: boolean) => void;
	children?: ReactNode;
	/**
	 * Accepted for call-site compatibility with Radix Dialog.
	 * Headless UI dialogs are modal (focus trap); non-modal marketing overlays
	 * should use `@radix-ui/react-dialog` Root + Portal directly.
	 */
	modal?: boolean;
}

function Dialog({
	open: openProp,
	defaultOpen = false,
	onOpenChange,
	children,
	modal: _modal,
}: DialogProps) {
	const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
	const isControlled = openProp !== undefined;
	const open = isControlled ? Boolean(openProp) : uncontrolledOpen;

	const setOpen = useCallback(
		(next: boolean) => {
			if (!isControlled) {
				setUncontrolledOpen(next);
			}
			onOpenChange?.(next);
		},
		[isControlled, onOpenChange],
	);

	const value = useMemo(() => ({ open, setOpen }), [open, setOpen]);

	return (
		<DialogContext.Provider value={value}>{children}</DialogContext.Provider>
	);
}

interface DialogTriggerProps extends ComponentPropsWithoutRef<"button"> {
	asChild?: boolean;
}

function DialogTrigger({
	asChild = false,
	onClick,
	...props
}: DialogTriggerProps) {
	const { setOpen } = useDialogContext("DialogTrigger");
	const Comp = asChild ? Slot : "button";

	return (
		<Comp
			type={asChild ? undefined : "button"}
			data-slot="dialog-trigger"
			onClick={(event) => {
				onClick?.(event);
				if (!event.defaultPrevented) {
					setOpen(true);
				}
			}}
			{...props}
		/>
	);
}

interface DialogDismissEvent {
	preventDefault: () => void;
	defaultPrevented: boolean;
	currentTarget: EventTarget | null;
}

function createDismissEvent(
	currentTarget: EventTarget | null = null,
): DialogDismissEvent {
	let defaultPrevented = false;
	return {
		currentTarget,
		preventDefault() {
			defaultPrevented = true;
		},
		get defaultPrevented() {
			return defaultPrevented;
		},
	};
}

interface DialogContentProps
	extends Omit<
		HTMLAttributes<HTMLDivElement>,
		| "onDrag"
		| "onDragStart"
		| "onDragEnd"
		| "onDragOver"
		| "onAnimationStart"
		| "onAnimationEnd"
	> {
	overlayClassName?: string;
	/** Default true — CircleButton `xs` close control (aligned across product dialogs). */
	showCloseButton?: boolean;
	/** Headless panel entrance direction. Default `top`. */
	from?: "top" | "bottom" | "left" | "right";
	style?: CSSProperties;
	/**
	 * Radix-compat focus handoff. `preventDefault()` disables Headless autoFocus;
	 * callers should focus their preferred control.
	 */
	onOpenAutoFocus?: (event: DialogDismissEvent) => void;
	/**
	 * Radix-compat stubs — Headless shares one `onClose` for Escape, outside,
	 * and Close. Non-dismissable dialogs should ignore `onOpenChange(false)`
	 * (see RunBudgetGateDialog). These props are accepted so call sites typecheck.
	 */
	onEscapeKeyDown?: (event: DialogDismissEvent) => void;
	onPointerDownOutside?: (event: DialogDismissEvent) => void;
	onInteractOutside?: (event: DialogDismissEvent) => void;
	onFocusOutside?: (event: DialogDismissEvent) => void;
	onCloseAutoFocus?: (event: DialogDismissEvent) => void;
}

function DialogContent({
	className,
	children,
	overlayClassName,
	showCloseButton = true,
	from = "top",
	onOpenAutoFocus,
	onEscapeKeyDown: _onEscapeKeyDown,
	onPointerDownOutside: _onPointerDownOutside,
	onInteractOutside: _onInteractOutside,
	onFocusOutside: _onFocusOutside,
	onCloseAutoFocus: _onCloseAutoFocus,
	...props
}: DialogContentProps) {
	const { open, setOpen } = useDialogContext("DialogContent");
	const panelRef = useRef<HTMLDivElement | null>(null);
	const [autoFocus, setAutoFocus] = useState(true);

	useEffect(() => {
		if (!open) {
			setAutoFocus(true);
			return;
		}
		if (!onOpenAutoFocus) {
			setAutoFocus(true);
			return;
		}
		const event = createDismissEvent(panelRef.current);
		onOpenAutoFocus(event);
		setAutoFocus(!event.defaultPrevented);
	}, [open, onOpenAutoFocus]);

	return (
		<DialogRoot
			open={open}
			onClose={() => {
				setOpen(false);
			}}
			autoFocus={autoFocus}
		>
			<HeadlessDialogBackdropPrimitive
				className={cn(
					"fixed inset-0 z-[200] bg-black/80 backdrop-blur-xs",
					overlayClassName,
				)}
			/>
			<HeadlessDialogPanelPrimitive
				ref={panelRef}
				from={from}
				className={cn(
					"fixed top-[50%] left-[50%] z-[200] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border bg-background p-6 shadow-lg md:w-full",
					className,
				)}
				{...(props as Record<string, unknown>)}
			>
				{children}
				{showCloseButton ? (
					<HeadlessDialogClose
						as={CircleButton}
						type="button"
						size="xs"
						chrome="solid"
						tone="system"
						icon={<XIcon />}
						aria-label="Close"
						className="absolute top-3 right-3 z-10"
					/>
				) : null}
			</HeadlessDialogPanelPrimitive>
		</DialogRoot>
	);
}

function DialogClose({
	asChild = false,
	onClick,
	...props
}: ComponentPropsWithoutRef<"button"> & { asChild?: boolean }) {
	if (asChild) {
		return (
			<HeadlessDialogClose as={Slot} onClick={onClick} {...props} />
		);
	}

	return (
		<HeadlessDialogClose
			type="button"
			data-slot="dialog-close"
			onClick={onClick}
			{...props}
		/>
	);
}

function DialogHeader({
	className,
	...props
}: HTMLAttributes<HTMLDivElement>) {
	return (
		<div
			data-slot="dialog-header"
			className={cn(
				"flex flex-col space-y-1.5 text-center sm:text-left",
				className,
			)}
			{...props}
		/>
	);
}

function DialogFooter({
	className,
	...props
}: HTMLAttributes<HTMLDivElement>) {
	return (
		<div
			data-slot="dialog-footer"
			className={cn(
				"flex w-full flex-row flex-wrap items-stretch justify-stretch gap-2",
				className,
			)}
			{...props}
		/>
	);
}

interface DialogFooterCtaProps {
	ctaWidth?: "auto" | "full" | "grow";
	ctaSize?: "micro" | "small" | "medium" | "large";
}

/**
 * Design-system dialog action row.
 * Children must be `PrimaryCta` / `SecondaryCta` / `DestructiveCta` (not raw `Button`).
 * Auto-applies `ctaWidth="grow"` for two actions (half width) or `"full"` for one.
 */
function DialogFooterActions({
	className,
	children,
}: {
	className?: string;
	children: ReactNode;
}) {
	const items = Children.toArray(children).filter(Boolean);
	const count = items.length;
	const defaultWidth = count <= 1 ? "full" : "grow";

	return (
		<DialogFooter className={className}>
			{items.map((child, index) => {
				if (!isValidElement(child)) {
					return (
						<span key={`dialog-footer-node-${index}`}>{child}</span>
					);
				}
				const element = child as ReactElement<DialogFooterCtaProps>;
				return cloneElement(element, {
					ctaWidth: element.props.ctaWidth ?? defaultWidth,
					ctaSize: element.props.ctaSize ?? "medium",
				});
			})}
		</DialogFooter>
	);
}

function DialogTitle({
	className,
	...props
}: ComponentPropsWithoutRef<typeof HeadlessDialogTitle>) {
	return (
		<HeadlessDialogTitle
			className={cn(
				"font-black text-lg leading-none tracking-wide uppercase",
				className,
			)}
			{...props}
		/>
	);
}

function DialogDescription({
	className,
	...props
}: ComponentPropsWithoutRef<typeof HeadlessDialogDescription>) {
	return (
		<HeadlessDialogDescription
			className={cn("text-sm text-muted-foreground", className)}
			{...props}
		/>
	);
}

export {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogFooterActions,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
};
